// Pure derivation of ManagedContractForm's seed state from a loaded
// ManagedContract. Extracted from the form's render-time seeding effect so
// the mapping is typed against a real (non-null) ManagedContract instead of
// `existingContract!` assertions, and is unit-testable in isolation.

import type {
  ManagedContract,
  ManagedContractType,
  PersonReference,
  ExtensionOptions,
  ExternalLink,
} from "../../types";
import { ownerOf, managerOf } from "../../types";
import type {
  GraceByFrequency,
  GraceMode,
} from "../../components/manage/ContractSettingsCard";

export interface ManagedContractFormState {
  type: ManagedContractType;
  contractNumber: string;
  title: string;
  description: string;
  supplierId: string;
  supplierName: string;
  contractTypeId: string;
  businessUnitId: string;
  procurementMethodId: string;
  startDate: string;
  endDate: string;
  value: number;
  contractManager: PersonReference | undefined;
  contractOwner: PersonReference | undefined;
  extensionOptions: ExtensionOptions | undefined;
  links: ExternalLink[];
  notes: string;
  complianceTarget: string;
  perfSuccessThreshold: string;
  perfWatchThreshold: string;
  graceMode: GraceMode;
  meetingGraceDays: string;
  graceByFreq: GraceByFrequency;
  /** Only meaningful when `gstAppliesTouched` is true — mirrors the source
   *  contract's stored intent. When false, the caller should leave whatever
   *  gstApplies/gstAppliesTouched state it already has untouched (matches
   *  the never-explicitly-set placeholder behaviour on display surfaces). */
  gstApplies: boolean;
  gstAppliesTouched: boolean;
  sowNumberFormat: "nn" | "sow-yyyy-nnn";
  variationNumberFormat: "nn" | "var-yyyy-nnn";
}

/** Maps a loaded ManagedContract to the flat string/boolean form state
 *  ManagedContractForm seeds its inputs from on edit. Number/supplier/dates
 *  can be absent on sparse stored rows — those seed as empty strings so
 *  submit validation prompts the user to complete the record rather than
 *  the seed step throwing. */
export function contractToFormState(
  contract: ManagedContract,
): ManagedContractFormState {
  const existingManager = managerOf(contract);
  const existingOwner = ownerOf(contract);

  const byFreq = contract.meetingGraceDaysByFrequency;
  const hasPerFreq =
    !!byFreq &&
    (typeof byFreq.daily === "number" ||
      typeof byFreq.weekly === "number" ||
      typeof byFreq.monthly === "number" ||
      typeof byFreq.quarterly === "number" ||
      typeof byFreq.annually === "number" ||
      typeof byFreq.oneOff === "number");
  const hasSingle = typeof contract.meetingGraceDays === "number";
  // Per-frequency map wins when both happen to be set on legacy data.
  const graceMode: GraceMode = hasPerFreq
    ? "by-freq"
    : hasSingle
      ? "single"
      : "default";

  const gstAppliesTouched = typeof contract.gstApplies === "boolean";

  return {
    type: contract.type,
    contractNumber: contract.contractNumber ?? "",
    title: contract.title,
    description: contract.description || "",
    supplierId: contract.supplierId,
    supplierName: contract.supplierName ?? "",
    contractTypeId: contract.contractTypeId,
    businessUnitId: contract.businessUnitId || "",
    procurementMethodId: contract.procurementMethodId || "",
    startDate: contract.startDate ?? "",
    endDate: contract.endDate ?? "",
    value: contract.value,
    contractManager: existingManager
      ? {
          name: existingManager.name,
          email: existingManager.email,
          alertsEnabled: existingManager.alertsEnabled,
          userId: existingManager.userId,
        }
      : undefined,
    contractOwner: existingOwner
      ? {
          name: existingOwner.name,
          email: existingOwner.email,
          alertsEnabled: existingOwner.alertsEnabled,
          userId: existingOwner.userId,
        }
      : undefined,
    extensionOptions: contract.extensionOptions,
    links: contract.links || [],
    notes: contract.notes || "",
    complianceTarget:
      typeof contract.complianceTarget === "number"
        ? String(contract.complianceTarget)
        : "",
    perfSuccessThreshold:
      typeof contract.performanceSuccessThreshold === "number"
        ? String(contract.performanceSuccessThreshold)
        : "",
    perfWatchThreshold:
      typeof contract.performanceWatchThreshold === "number"
        ? String(contract.performanceWatchThreshold)
        : "",
    graceMode,
    meetingGraceDays:
      typeof contract.meetingGraceDays === "number"
        ? String(contract.meetingGraceDays)
        : "",
    graceByFreq: {
      daily: byFreq?.daily !== undefined ? String(byFreq.daily) : "",
      weekly: byFreq?.weekly !== undefined ? String(byFreq.weekly) : "",
      monthly: byFreq?.monthly !== undefined ? String(byFreq.monthly) : "",
      quarterly:
        byFreq?.quarterly !== undefined ? String(byFreq.quarterly) : "",
      annually: byFreq?.annually !== undefined ? String(byFreq.annually) : "",
      oneOff: byFreq?.oneOff !== undefined ? String(byFreq.oneOff) : "",
    },
    gstApplies: gstAppliesTouched ? (contract.gstApplies as boolean) : false,
    gstAppliesTouched,
    sowNumberFormat: contract.sowNumberFormat ?? "nn",
    variationNumberFormat: contract.variationNumberFormat ?? "nn",
  };
}
